/**
 * ObstructionFilters Component
 *
 * Filter panel for the obstructions sidebar. Mirrors `TaskFilters` shape but
 * widens the status / type / severity / impact controls to multi-select (the
 * obstruction filter slice §1.4 accepts arrays for these dimensions).
 *
 * Atomic Zustand subscriptions only — `useShallow` for array filter slots.
 */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconAlertTriangle,
  IconBuilding,
  IconCalendar,
  IconFlag,
  IconListCheck,
  IconRoad,
  IconRoute,
} from '@tabler/icons-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { selectHasActiveObstructionFilters } from '@/store/selectors/obstructionFilterSelectors';
import { getDateInputPattern } from '@/config/dateLocales';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';
import { SortControls } from '@/components/ui/SortControls';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { OBSTRUCTION_SORT_OPTIONS } from '@/constants/sortOptions';
import {
  ObstructionStatus,
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import type {
  ObstructionSortDir,
  ObstructionSortField,
} from '@/store/slices/obstructionFiltersSlice';
import styles from './ObstructionFilters.module.css';

const SORT_DIR_ASC: ObstructionSortDir = 'asc';
const SORT_DIR_DESC: ObstructionSortDir = 'desc';

const OBSTRUCTION_MULTISELECT_COMBOBOX_PROPS = {
  zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
  withinPortal: true,
} as const;

const OBSTRUCTION_FILTER_POPOVER_PROPS = {
  zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
  withinPortal: true,
} as const;

export const ObstructionFilters = memo(function ObstructionFilters() {
  const { t, i18n } = useTranslation();

  // Atomic state subscriptions
  const types = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.types)
  );
  const statuses = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.statuses)
  );
  const severities = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.severities)
  );
  const impacts = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.impacts)
  );
  const location = useGeoMarkupStore(
    (state) => state.obstructionFilters.location
  );
  const contractor = useGeoMarkupStore(
    (state) => state.obstructionFilters.contractor
  );
  const dateFrom = useGeoMarkupStore(
    (state) => state.obstructionFilters.dateFrom
  );
  const dateTo = useGeoMarkupStore((state) => state.obstructionFilters.dateTo);
  const sortBy = useGeoMarkupStore((state) => state.obstructionFilters.sortBy);
  const sortDir = useGeoMarkupStore(
    (state) => state.obstructionFilters.sortDir
  );
  const hasActiveFilters = useGeoMarkupStore(selectHasActiveObstructionFilters);

  // Atomic action subscriptions (stable references → zero re-renders)
  const setTypes = useGeoMarkupStore(
    (state) => state.obstructionFilters.setTypes
  );
  const setStatuses = useGeoMarkupStore(
    (state) => state.obstructionFilters.setStatuses
  );
  const setSeverities = useGeoMarkupStore(
    (state) => state.obstructionFilters.setSeverities
  );
  const setImpacts = useGeoMarkupStore(
    (state) => state.obstructionFilters.setImpacts
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.obstructionFilters.setLocation
  );
  const setContractor = useGeoMarkupStore(
    (state) => state.obstructionFilters.setContractor
  );
  const setDateRange = useGeoMarkupStore(
    (state) => state.obstructionFilters.setDateRange
  );
  const setSortBy = useGeoMarkupStore(
    (state) => state.obstructionFilters.setSortBy
  );
  const setSortDir = useGeoMarkupStore(
    (state) => state.obstructionFilters.setSortDir
  );
  const resetFilters = useGeoMarkupStore(
    (state) => state.obstructionFilters.resetFilters
  );

  // Debounced location / contractor text inputs
  const [localLocation, setLocalLocation] = useState(location);
  const { debouncedValue: debouncedLocation } = useDebounce(
    localLocation,
    SEARCH_DEBOUNCE_DELAY
  );
  const isExternalLocationChange = useRef(false);

  useEffect(() => {
    isExternalLocationChange.current = true;
    setLocalLocation(location);
  }, [location]);

  useEffect(() => {
    if (isExternalLocationChange.current) {
      isExternalLocationChange.current = false;
      return;
    }
    if (debouncedLocation !== location) {
      setLocation(debouncedLocation);
    }
  }, [debouncedLocation, location, setLocation]);

  const [localContractor, setLocalContractor] = useState(contractor);
  const { debouncedValue: debouncedContractor } = useDebounce(
    localContractor,
    SEARCH_DEBOUNCE_DELAY
  );
  const isExternalContractorChange = useRef(false);

  useEffect(() => {
    isExternalContractorChange.current = true;
    setLocalContractor(contractor);
  }, [contractor]);

  useEffect(() => {
    if (isExternalContractorChange.current) {
      isExternalContractorChange.current = false;
      return;
    }
    if (debouncedContractor !== contractor) {
      setContractor(debouncedContractor);
    }
  }, [debouncedContractor, contractor, setContractor]);

  // Options
  const typeOptions = useMemo(
    () =>
      Object.values(ObstructionType).map((value) => ({
        value,
        label: t(`obstructions.type.${value}`),
      })),
    [t]
  );

  const statusOptions = useMemo(
    () =>
      Object.values(ObstructionStatus).map((value) => ({
        value,
        label: t(`obstructions.status.${value}`),
      })),
    [t]
  );

  const severityOptions = useMemo(
    () =>
      Object.values(SeverityLevel).map((value) => ({
        value,
        label: t(`obstructions.severity.${value}`),
      })),
    [t]
  );

  const impactOptions = useMemo(
    () =>
      Object.values(TrafficImpact).map((value) => ({
        value,
        label: t(`obstructions.impact.${value}`),
      })),
    [t]
  );

  const dateFormat = useMemo(
    () => getDateInputPattern(i18n.language).pattern,
    [i18n.language]
  );

  // Handlers
  const handleTypesChange = useCallback(
    (values: string[]) => setTypes(values as ObstructionType[]),
    [setTypes]
  );

  const handleStatusesChange = useCallback(
    (values: string[]) => setStatuses(values as ObstructionStatus[]),
    [setStatuses]
  );

  const handleSeveritiesChange = useCallback(
    (values: string[]) => setSeverities(values as SeverityLevel[]),
    [setSeverities]
  );

  const handleImpactsChange = useCallback(
    (values: string[]) => setImpacts(values as TrafficImpact[]),
    [setImpacts]
  );

  const handleLocationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalLocation(event.currentTarget.value);
    },
    []
  );

  const handleContractorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalContractor(event.currentTarget.value);
    },
    []
  );

  const handleDateFromChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(null, dateTo);
        return;
      }
      if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(isNaN(parsed.getTime()) ? null : parsed, dateTo);
        return;
      }
      setDateRange(value, dateTo);
    },
    [setDateRange, dateTo]
  );

  const handleDateToChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(dateFrom, null);
        return;
      }
      if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(dateFrom, isNaN(parsed.getTime()) ? null : parsed);
        return;
      }
      setDateRange(dateFrom, value);
    },
    [setDateRange, dateFrom]
  );

  const handleSortByChange = useCallback(
    (value: string) => setSortBy(value as ObstructionSortField),
    [setSortBy]
  );

  const handleSortDirectionChange = useCallback(
    (descending: boolean) =>
      setSortDir(descending ? SORT_DIR_DESC : SORT_DIR_ASC),
    [setSortDir]
  );

  const sortDescending = sortDir === SORT_DIR_DESC;

  return (
    <div className={styles.container}>
      <div className={styles.fieldsRow}>
        <CollapsingMultiSelect
          className={styles.fieldWide}
          label={t('obstructions.filters.typeLabel')}
          placeholder={t('obstructions.filters.typePlaceholder')}
          data={typeOptions}
          value={types}
          onChange={handleTypesChange}
          leftSection={<IconAlertTriangle size={ICON_SIZE_SM} />}
          comboboxProps={OBSTRUCTION_MULTISELECT_COMBOBOX_PROPS}
          clearable
        />

        <CollapsingMultiSelect
          className={styles.fieldWide}
          label={t('obstructions.filters.statusLabel')}
          placeholder={t('obstructions.filters.statusPlaceholder')}
          data={statusOptions}
          value={statuses}
          onChange={handleStatusesChange}
          leftSection={<IconListCheck size={ICON_SIZE_SM} />}
          comboboxProps={OBSTRUCTION_MULTISELECT_COMBOBOX_PROPS}
          clearable
        />

        <CollapsingMultiSelect
          className={styles.fieldWide}
          label={t('obstructions.filters.severityLabel')}
          placeholder={t('obstructions.filters.severityPlaceholder')}
          data={severityOptions}
          value={severities}
          onChange={handleSeveritiesChange}
          leftSection={<IconFlag size={ICON_SIZE_SM} />}
          comboboxProps={OBSTRUCTION_MULTISELECT_COMBOBOX_PROPS}
          clearable
        />

        <CollapsingMultiSelect
          className={styles.fieldWide}
          label={t('obstructions.filters.impactLabel')}
          placeholder={t('obstructions.filters.impactPlaceholder')}
          data={impactOptions}
          value={impacts}
          onChange={handleImpactsChange}
          leftSection={<IconRoute size={ICON_SIZE_SM} />}
          comboboxProps={OBSTRUCTION_MULTISELECT_COMBOBOX_PROPS}
          clearable
        />

        <TextInput
          className={styles.fieldWide}
          label={t('obstructions.filters.location')}
          placeholder={t('obstructions.filters.locationPlaceholder')}
          leftSection={<IconRoad size={ICON_SIZE_SM} />}
          value={localLocation}
          onChange={handleLocationChange}
        />

        <TextInput
          className={styles.fieldWide}
          label={t('obstructions.filters.contractor')}
          placeholder={t('obstructions.filters.contractorPlaceholder')}
          leftSection={<IconBuilding size={ICON_SIZE_SM} />}
          value={localContractor}
          onChange={handleContractorChange}
        />

        <DateInput
          className={styles.dateField}
          label={t('obstructions.filters.dateFrom')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateFrom}
          onChange={handleDateFromChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          maxDate={dateTo ?? undefined}
          popoverProps={OBSTRUCTION_FILTER_POPOVER_PROPS}
        />

        <DateInput
          className={styles.dateField}
          label={t('obstructions.filters.dateTo')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateTo}
          onChange={handleDateToChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          minDate={dateFrom ?? undefined}
          popoverProps={OBSTRUCTION_FILTER_POPOVER_PROPS}
        />
      </div>

      <div className={styles.sortRow}>
        <SortControls
          options={OBSTRUCTION_SORT_OPTIONS}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSortByChange={handleSortByChange}
          onSortDescendingChange={handleSortDirectionChange}
        />
      </div>

      {hasActiveFilters && (
        <div className={styles.clearRow}>
          <ClearFiltersButton onClick={resetFilters} />
        </div>
      )}
    </div>
  );
});
